import React from 'react';
import { layout, palette } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { CustomLinkBanner } from 'App/CustomLinkBanner/CustomLinkBanner';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import { Actions } from 'App/Header/Actions';
import { SmallFormTitle } from 'App/Header/SmallFormTitle';
import { Title } from 'App/Header/Title';
import { ItemSearchBar } from 'App/Masthead/ItemSearchBar';
import { OfflineBanner } from 'Components/AppLayout';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { Banner } from './Banner';
import { useFormFactor } from './useFormFactor';

const StyledContent = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
`;

const StyledAppHeaderContainer = styled.div`
	max-width: 1280px;
	margin: 0 auto;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	width: 100%;
	border-radius: 0px 0px ${layout.smallSpace} ${layout.smallSpace};
	border: solid ${palette.grey300};
	border-width: 0px 1px 1px 1px;
	overflow: hidden;

	& > *:last-child {
		border-width: 0px;
	}

	@media (max-width: 1280px) {
		border-radius: 0px;
	}
`;

interface HeaderContentProps {
	isLargeFormFactor: boolean;
}

const CustomLinkSection = () => {
	const isCustomLinkEnabled = useFeatureCanary(FeatureFlag.EnableCustomLinks);
	const customLinkData =
		useConfigurationContext().workspaceConfiguration?.pinnedLinks?.pinnedLink || [];
	const { isSmallFormFactor } = useFormFactor();

	if (!isCustomLinkEnabled || !customLinkData.length) {
		return null;
	}

	return (
		<CustomLinkBanner
			customLinkData={customLinkData}
			isSmallFormFactor={isSmallFormFactor}
		/>
	);
};

const HeaderContent = ({ isLargeFormFactor }: HeaderContentProps) => (
	<>
		{!environment.citrixCloudConnected && <OfflineBanner />}
		<Banner>
			<StyledContent data-testid="header-content">
				{isLargeFormFactor ? <Title /> : <SmallFormTitle />}
				{isLargeFormFactor && <ItemSearchBar />}
				<Actions />
			</StyledContent>
			{!isLargeFormFactor && <ItemSearchBar />}
		</Banner>
		{isLargeFormFactor && <CustomLinkSection />}
	</>
);

export const _DaasHeader = () => {
	const isDetailedClassicViewEnabled = useFeatureCanary(
		FeatureFlag.DetailedDesktopClassicView
	);
	const { isLargeFormFactor } = useFormFactor();

	return isDetailedClassicViewEnabled && isLargeFormFactor ? (
		<StyledAppHeaderContainer data-testid="app-header-container">
			<HeaderContent isLargeFormFactor={isLargeFormFactor} />
		</StyledAppHeaderContainer>
	) : (
		<>
			<HeaderContent isLargeFormFactor={isLargeFormFactor} />
		</>
	);
};

export const DaasHeader = () => {
	return (
		<GenericErrorBoundary renderProp={() => <_DaasHeader />} name="DaasHeaderError" />
	);
};
